import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarCheck, X } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useEquipesParaFiltro, useTarefasPainel } from '@/hooks/useTarefas';

/* O SELO DA IA (Douglas 30/09/2026): vermelho, para saber de longe que a
   tarefa do consultor foi aberta pela IsabellA. */
export const SELO_IA = 'inline-block rounded bg-[#C0392B] px-1.5 text-[9px] font-bold uppercase tracking-[0.04em] text-white';
const NUM_IA = 'inline-block min-w-[18px] rounded bg-[#C0392B] px-1 text-center text-[11px] font-semibold tabular-nums text-white';

const primeiro = (n?: string | null) => {
  const p = String(n || '').split(' ')[0];
  return p ? p.charAt(0) + p.slice(1).toLowerCase() : '—';
};

/**
 * PAINEL DE TAREFAS DO TOPO (Douglas 30/09/2026): "no lugar da nota quero ver
 * quem está abrindo as tarefas, e pra quem, e quem não tem finalizada".
 * Quatro números, quem mais abre (qualquer cargo, a IA com selo) e quem não
 * finaliza (só consultor dono, com quantas a IA abriu para ele). O clique num
 * nome abre a página Tarefas filtrada por essa pessoa.
 */
export function PainelTarefasTopo({ aberto, onFechar, podeFiltrarEquipe }: {
  aberto: boolean;
  onFechar: () => void;
  podeFiltrarEquipe: boolean;
}) {
  const navigate = useNavigate();
  const [dias, setDias] = useState(30);
  const [equipeId, setEquipeId] = useState<string | null>(null);
  const painel = useTarefasPainel(dias, equipeId, aberto);
  const equipes = useEquipesParaFiltro(aberto && podeFiltrarEquipe);
  const d = painel.data;

  const abrirPessoa = (id: string | null, nome: string | null) => {
    onFechar();
    if (!id) { navigate('/tarefas'); return; }
    navigate(`/tarefas?responsavel=${encodeURIComponent(id)}&nome=${encodeURIComponent(primeiro(nome))}`);
  };

  const filtro = 'h-[26px] rounded-lg border border-white/[0.18] bg-white/[0.12] px-2 text-[11px] text-white focus:outline-none [&>option]:text-foreground';
  const th = 'px-1 py-1.5 text-left text-[10px] font-bold uppercase tracking-[0.6px] text-muted-foreground';
  const td = 'px-1 py-1.5 border-t border-border/60';
  const tile = 'rounded-lg bg-[#FAF8F4] px-2.5 py-2 dark:bg-[#241A1A]';

  return (
    <Dialog open={aberto} onOpenChange={(v) => { if (!v) onFechar(); }}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-[680px] max-h-[88vh] flex flex-col overflow-hidden p-0 gap-0 rounded-2xl sm:rounded-2xl bg-background [&>button]:hidden">
        <div className="flex shrink-0 items-center gap-3 bg-[linear-gradient(135deg,#4A1320_0%,#5B0A0D_55%,#6E1626_100%)] px-4 py-3">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[9px] bg-white/[0.12] text-white"><CalendarCheck className="h-4 w-4" /></span>
          <div className="min-w-0 flex-1">
            <DialogTitle className="font-display text-[15px] font-bold tracking-[-0.3px] text-white">Tarefas · quem abre e quem finaliza</DialogTitle>
            <p className="truncate text-[10.5px] text-white/65">Últimos {dias} dias · {equipeId ? (equipes.data?.find((e) => e.id === equipeId)?.nome ?? 'equipe') : 'todas as equipes que você vê'}</p>
          </div>
          <select value={dias} onChange={(e) => setDias(Number(e.target.value))} className={filtro} aria-label="Período">
            <option value={7}>7 dias</option><option value={30}>30 dias</option><option value={90}>90 dias</option>
          </select>
          {podeFiltrarEquipe && (
            <select value={equipeId ?? ''} onChange={(e) => setEquipeId(e.target.value || null)} className={filtro} aria-label="Equipe">
              <option value="">Equipe</option>
              {(equipes.data ?? []).map((e) => <option key={e.id} value={e.id}>{e.nome}</option>)}
            </select>
          )}
          <button type="button" aria-label="Fechar" onClick={onFechar}
            className="grid h-[26px] w-[26px] place-items-center rounded-lg border border-white/[0.14] bg-white/[0.12] text-white hover:bg-white/[0.22]">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
          {painel.isLoading && <p className="text-[11px] text-muted-foreground">carregando…</p>}
          {painel.error && <p role="alert" className="text-[11px] text-destructive">{(painel.error as Error).message}</p>}
          {d && <>
            <div className="grid grid-cols-4 gap-2">
              <div className={tile}><p className="text-[10.5px] text-muted-foreground">Abertas</p><p className="font-display text-[20px] font-bold tabular-nums">{d.abertas}</p></div>
              <div className={tile}><p className="text-[10.5px] text-muted-foreground">Feitas</p><p className="font-display text-[20px] font-bold tabular-nums text-success">{d.feitas}</p></div>
              <div className={tile}><p className="text-[10.5px] text-muted-foreground">Vencidas sem finalizar</p><p className="font-display text-[20px] font-bold tabular-nums text-primary">{d.vencidas}</p></div>
              <div className={tile}><p className="text-[10.5px] text-muted-foreground">Abertas pela IA</p><p className="font-display text-[20px] font-bold tabular-nums">{d.pela_ia} <span className={SELO_IA}>IA</span></p></div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <p className="mb-1 text-[12px] font-semibold">Quem mais abre <span className="font-normal text-muted-foreground">· qualquer cargo</span></p>
                <table className="w-full table-fixed text-[12px]">
                  <thead><tr><th className={cn(th, 'w-[44%]')}>Abriu</th><th className={cn(th, 'text-right')}>Abertas</th><th className={cn(th, 'text-right')}>Feitas</th><th className={cn(th, 'text-right')}>Vencidas</th></tr></thead>
                  <tbody>
                    {d.quem_abre.map((l, i) => (
                      <tr key={`${l.nome}-${i}`} onClick={() => abrirPessoa(l.ia ? null : l.usuario_id, l.nome)} className="cursor-pointer hover:bg-muted/50">
                        <td className={cn(td, 'truncate')}>
                          {l.ia ? <>IsabellA <span className={SELO_IA}>IA</span></> : <>{primeiro(l.nome)} <span className="text-[10px] text-muted-foreground">{l.cargo}</span></>}
                        </td>
                        <td className={cn(td, 'text-right tabular-nums')}>{l.abertas}</td>
                        <td className={cn(td, 'text-right tabular-nums', l.feitas > 0 && 'text-success')}>{l.feitas}</td>
                        <td className={cn(td, 'text-right tabular-nums font-semibold text-primary')}>{l.vencidas}</td>
                      </tr>
                    ))}
                    {!d.quem_abre.length && <tr><td colSpan={4} className={cn(td, 'text-muted-foreground')}>Nenhuma tarefa no período.</td></tr>}
                  </tbody>
                </table>
              </div>
              <div>
                <p className="mb-1 text-[12px] font-semibold">Quem não finaliza <span className="font-normal text-muted-foreground">· só consultor dono</span></p>
                <table className="w-full table-fixed text-[12px]">
                  <thead><tr><th className={cn(th, 'w-[34%]')}>Consultor</th><th className={cn(th, 'text-right')}>Recebeu</th><th className={cn(th, 'text-right')}>IA</th><th className={cn(th, 'text-right')}>Feitas</th><th className={cn(th, 'text-right')}>Venc.</th></tr></thead>
                  <tbody>
                    {d.quem_nao_finaliza.map((l) => (
                      <tr key={l.usuario_id} onClick={() => abrirPessoa(l.usuario_id, l.nome)} className="cursor-pointer hover:bg-muted/50">
                        <td className={cn(td, 'truncate')}>{primeiro(l.nome)}</td>
                        <td className={cn(td, 'text-right tabular-nums')}>{l.recebeu}</td>
                        <td className={cn(td, 'text-right')}>{l.pela_ia > 0 ? <span className={NUM_IA}>{l.pela_ia}</span> : <span className="text-muted-foreground">0</span>}</td>
                        <td className={cn(td, 'text-right tabular-nums', l.feitas > 0 && 'text-success')}>{l.feitas}</td>
                        <td className={cn(td, 'text-right tabular-nums font-semibold text-primary')}>{l.vencidas}</td>
                      </tr>
                    ))}
                    {!d.quem_nao_finaliza.length && <tr><td colSpan={5} className={cn(td, 'text-muted-foreground')}>Nenhuma tarefa de consultor no período.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>

            <p className="text-[11px] text-muted-foreground">
              A tarefa é sempre do consultor dono: quem abriu (IA, supervisor, BKO, Qualidade ou gestão) ajuda, e o consultor finaliza.
              A coluna <span className={NUM_IA}>IA</span> mostra quantas das tarefas dele foram abertas pela IsabellA. Clique num nome: abre a página Tarefas filtrada por essa pessoa, com as vencidas no topo.
            </p>
          </>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
